import { useEffect, useState } from 'react';
import { Clock, Sparkles } from 'lucide-react';

// SLA windows per tier — upper bounds so we never over-promise.
const TIER_SLA_HOURS = {
  basic: 5 * 24,     // "3-5 day response"
  premium: 2 * 24,   // "1-2 day response"
  indepth: 24,       // "Same-day response" — 24h upper bound
};

const TIER_LABEL = {
  basic: 'within 5 days',
  premium: 'within 2 days',
  indepth: 'within 24 hours',
};

function formatRemaining(ms) {
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  const seconds = Math.floor((ms % 60000) / 1000);

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (days > 0 || hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  if (days === 0) parts.push(`${seconds}s`);
  return parts.join(' ');
}

export default function ResponseETA({ consultation }) {
  const [now, setNow] = useState(() => Date.now());

  const startedAtRaw = consultation?.paid_at || consultation?.created_at;
  const tier = consultation?.tier || 'basic';
  const slaHours = TIER_SLA_HOURS[tier] ?? TIER_SLA_HOURS.basic;
  const startedAt = startedAtRaw ? new Date(startedAtRaw).getTime() : null;
  const etaTime = startedAt ? startedAt + slaHours * 3600 * 1000 : null;
  const remaining = etaTime ? etaTime - now : 0;
  const overdue = remaining <= 0;

  useEffect(() => {
    if (!etaTime || overdue) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [etaTime, overdue]);

  if (!startedAt) return null;

  if (overdue) {
    return (
      <div
        className="p-6 rounded-2xl flex items-start gap-4"
        style={{ backgroundColor: '#E8C26515', border: '1px solid #E8C26540' }}
        data-testid="response-eta-overdue"
      >
        <div className="p-3 rounded-2xl shrink-0" style={{ backgroundColor: '#E8C26520' }}>
          <Sparkles className="w-6 h-6" style={{ color: '#E8C265' }} />
        </div>
        <div>
          <p className="font-semibold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
            Your response is being lovingly prepared
          </p>
          <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.6 }}>
            Thank you for your patience — we're putting the final touches on your personalized guidance and it will land in your inbox very soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="p-6 rounded-2xl flex items-start gap-4"
      style={{ backgroundColor: '#78A18E15', border: '1px solid #78A18E40' }}
      data-testid="response-eta-countdown"
    >
      <div className="p-3 rounded-2xl shrink-0" style={{ backgroundColor: '#78A18E25' }}>
        <Clock className="w-6 h-6" style={{ color: '#78A18E' }} />
      </div>
      <div className="flex-1">
        <p className="font-semibold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
          Your response arrives in{' '}
          <span data-testid="response-eta-remaining" style={{ color: '#78A18E' }}>{formatRemaining(remaining)}</span>
        </p>
        <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.6 }}>
          Your <strong style={{ textTransform: 'capitalize' }}>{tier}</strong> consultation is promised {TIER_LABEL[tier] || 'soon'}. We'll email you the moment it's ready.
        </p>
      </div>
    </div>
  );
}
